const FINE_POINTER = '(hover: hover) and (pointer: fine)';
const MOBILE_GALLERY = '(max-width: 1023px)';
const MOVE_THRESHOLD = 8;
const SWIPE_THRESHOLD = 3;
const ZOOM_SCALE = 2;

class ProductZoom {
  constructor(container) {
    this.container = container;
    this.content = container.querySelector('[data-product-zoom-content]');
    this.button = container.querySelector('[data-product-zoom-button]');
    this.zoomInLabel = container.dataset.zoomInLabel;
    this.zoomOutLabel = container.dataset.zoomOutLabel;
    this.zoomCanvas = null;
    this.pointerStart = null;
    this.suppressClick = false;
    this.galleryTouchStart = null;
    this.gallerySettleTimer = null;
    this.panX = 0;
    this.panY = 0;
    this.zoomTouchStart = null;
    this.previousButton = container.querySelector('[data-product-images-previous]');
    this.nextButton = container.querySelector('[data-product-images-next]');

    if (!this.content || !this.content.querySelector('img[data-zoom-src]')) return;

    this.handleClick = this.handleClick.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
    this.handlePointerDown = this.handlePointerDown.bind(this);
    this.handlePointerMove = this.handlePointerMove.bind(this);
    this.handlePointerUp = this.handlePointerUp.bind(this);
    this.handleZoomTouchStart = this.handleZoomTouchStart.bind(this);
    this.handleZoomTouchMove = this.handleZoomTouchMove.bind(this);
    this.handleZoomTouchEnd = this.handleZoomTouchEnd.bind(this);
    this.close = this.close.bind(this);
    this.updateNavigation = this.updateNavigation.bind(this);

    container.addEventListener('click', this.handleClick);
    container.addEventListener('keydown', this.handleKeydown);
    container.addEventListener('pointerdown', this.handlePointerDown);
    container.addEventListener('pointermove', this.handlePointerMove);
    container.addEventListener('pointerup', this.handlePointerUp);
    container.addEventListener('pointercancel', this.handlePointerUp);
    container.addEventListener('touchstart', this.handleZoomTouchStart, { passive: true });
    container.addEventListener('touchmove', this.handleZoomTouchMove, { passive: false });
    container.addEventListener('touchend', this.handleZoomTouchEnd, { passive: true });
    container.addEventListener('touchcancel', this.handleZoomTouchEnd, { passive: true });
    container.addEventListener('mouseleave', () => {
      if (window.matchMedia(FINE_POINTER).matches) this.close();
    });
    window.addEventListener('resize', this.close);

    if (this.previousButton && this.nextButton) {
      this.previousButton.addEventListener('click', (event) => this.navigate(event, -1));
      this.nextButton.addEventListener('click', (event) => this.navigate(event, 1));
      this.content.addEventListener('scroll', this.updateNavigation, { passive: true });
      this.content.addEventListener('touchstart', (event) => this.handleGalleryTouchStart(event), { passive: true });
      this.content.addEventListener('touchend', (event) => this.handleGalleryTouchEnd(event), { passive: false });
      this.content.addEventListener('touchcancel', () => { this.galleryTouchStart = null; }, { passive: true });
      window.addEventListener('resize', this.updateNavigation);
      this.updateNavigation();
    }
  }

  get isZoomed() {
    return this.container.classList.contains('is-zoomed');
  }

  handleClick(event) {
    if (event.target.closest('[data-product-images-previous], [data-product-images-next]')) return;

    if (this.suppressClick) {
      this.suppressClick = false;
      return;
    }

    if (this.isZoomed) {
      this.close();
      return;
    }

    const fromButton = Boolean(event.target.closest('[data-product-zoom-button]'));
    this.open(fromButton ? undefined : event.clientX, fromButton ? undefined : event.clientY, event.target);
  }

  handleKeydown(event) {
    if (event.key === 'Escape') this.close();
  }

  handlePointerDown(event) {
    if (!this.isZoomed && window.matchMedia(FINE_POINTER).matches) return;
    this.pointerStart = {
      x: event.clientX,
      y: event.clientY,
      panX: this.panX,
      panY: this.panY,
    };

    if (this.isZoomed && event.isPrimary) {
      this.container.setPointerCapture(event.pointerId);
    }
  }

  handlePointerMove(event) {
    if (this.pointerStart && !window.matchMedia(FINE_POINTER).matches) {
      const movedX = Math.abs(event.clientX - this.pointerStart.x);
      const movedY = Math.abs(event.clientY - this.pointerStart.y);
      if (movedX > MOVE_THRESHOLD || movedY > MOVE_THRESHOLD) this.suppressClick = true;
    }

    if (!this.isZoomed) return;

    if (window.matchMedia(MOBILE_GALLERY).matches) {
      if (this.pointerStart) this.updatePan(event.clientX, event.clientY);
      return;
    }

    this.updatePosition(event.clientX, event.clientY);
  }

  handlePointerUp(event) {
    if (event && this.container.hasPointerCapture(event.pointerId)) {
      this.container.releasePointerCapture(event.pointerId);
    }
    this.pointerStart = null;
  }

  handleZoomTouchStart(event) {
    if (!this.isZoomed) return;
    const touch = event.touches[0];
    if (!touch) return;

    this.zoomTouchStart = {
      x: touch.clientX,
      y: touch.clientY,
      panX: this.panX,
      panY: this.panY,
    };
  }

  handleZoomTouchMove(event) {
    if (!this.isZoomed || !this.zoomTouchStart) return;
    const touch = event.touches[0];
    if (!touch) return;

    if (event.cancelable) event.preventDefault();
    const distanceX = touch.clientX - this.zoomTouchStart.x;
    const distanceY = touch.clientY - this.zoomTouchStart.y;
    if (Math.abs(distanceX) > MOVE_THRESHOLD || Math.abs(distanceY) > MOVE_THRESHOLD) this.suppressClick = true;

    this.setPan(
      this.zoomTouchStart.panX + distanceX,
      this.zoomTouchStart.panY + distanceY,
    );
  }

  handleZoomTouchEnd() {
    this.zoomTouchStart = null;
  }

  navigate(event, direction) {
    if (event.cancelable) event.preventDefault();
    event.stopPropagation();
    this.close();

    this.goToSlide(this.getCurrentSlideIndex() + direction);
  }

  handleGalleryTouchStart(event) {
    if (this.isZoomed || !window.matchMedia(MOBILE_GALLERY).matches) return;
    const touch = event.touches[0];
    if (!touch) return;

    this.galleryTouchStart = {
      x: touch.clientX,
      y: touch.clientY,
      index: this.getCurrentSlideIndex(),
    };
  }

  handleGalleryTouchEnd(event) {
    if (this.isZoomed || !this.galleryTouchStart) return;
    const touch = event.changedTouches[0];
    if (!touch) return;

    const distanceX = touch.clientX - this.galleryTouchStart.x;
    const distanceY = touch.clientY - this.galleryTouchStart.y;
    const startIndex = this.galleryTouchStart.index;
    this.galleryTouchStart = null;

    if (Math.abs(distanceX) < SWIPE_THRESHOLD || Math.abs(distanceX) <= Math.abs(distanceY)) return;
    if (event.cancelable) event.preventDefault();
    this.suppressClick = true;
    this.goToSlide(startIndex + (distanceX < 0 ? 1 : -1), true);
  }

  getSlides() {
    return Array.from(this.content.querySelectorAll('.product__image-zoom'));
  }

  getCurrentSlideIndex() {
    const slides = this.getSlides();
    if (!slides.length || !this.content.clientWidth) return 0;
    return Math.max(0, Math.min(slides.length - 1, Math.round(this.content.scrollLeft / this.content.clientWidth)));
  }

  goToSlide(index, fromSwipe = false) {
    const slides = this.getSlides();
    if (!slides.length) return;

    const target = (index + slides.length) % slides.length;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (fromSwipe) {
      window.clearTimeout(this.gallerySettleTimer);
      this.content.classList.add('is-settling');
      this.gallerySettleTimer = window.setTimeout(() => {
        this.content.classList.remove('is-settling');
      }, reduceMotion ? 0 : 400);
    }

    this.content.scrollTo({
      left: slides[target].offsetLeft,
      behavior: reduceMotion ? 'auto' : 'smooth',
    });
  }

  updateNavigation() {
    if (!this.previousButton || !this.nextButton) return;

    const isMobileGallery = window.matchMedia(MOBILE_GALLERY).matches;
    this.previousButton.disabled = !isMobileGallery;
    this.nextButton.disabled = !isMobileGallery;
  }

  open(clientX, clientY, eventTarget) {
    this.close();
    this.container.classList.add('is-zoomed');

    const tappedSlide = eventTarget?.closest('.product__image-zoom');
    const mobileSlide = tappedSlide || this.getCurrentSlide();
    this.zoomCanvas = window.matchMedia(MOBILE_GALLERY).matches && mobileSlide
      ? mobileSlide.cloneNode(true)
      : this.content.cloneNode(true);
    this.zoomCanvas.removeAttribute('data-product-zoom-content');
    this.zoomCanvas.classList.add('product__zoom-canvas');
    this.zoomCanvas.setAttribute('aria-hidden', 'true');

    const zoomImages = Array.from(this.zoomCanvas.querySelectorAll('img[data-zoom-src]'));
    const imageLoads = zoomImages.map((image) => {
      image.removeAttribute('srcset');
      image.removeAttribute('sizes');
      image.src = image.dataset.zoomSrc;

      if (image.complete) return Promise.resolve();
      return new Promise((resolve) => {
        image.addEventListener('load', resolve, { once: true });
        image.addEventListener('error', resolve, { once: true });
      });
    });

    this.container.appendChild(this.zoomCanvas);
    Promise.all(imageLoads).then(() => this.zoomCanvas?.classList.add('is-ready'));

    if (this.button) {
      this.button.setAttribute('aria-pressed', 'true');
      this.button.setAttribute('aria-label', this.zoomOutLabel);
    }

    this.updatePosition(clientX, clientY);
  }

  getCurrentSlide() {
    const slides = this.getSlides();
    if (!slides.length) return null;
    return slides[this.getCurrentSlideIndex()];
  }

  updatePosition(clientX, clientY) {
    const rect = this.container.getBoundingClientRect();
    const x = Number.isFinite(clientX) ? (clientX - rect.left) / rect.width : 0.5;
    const y = Number.isFinite(clientY) ? (clientY - rect.top) / rect.height : 0.5;
    const boundedX = Math.max(0, Math.min(1, x));
    const boundedY = Math.max(0, Math.min(1, y));
    const overflowX = rect.width * (ZOOM_SCALE - 1);
    const overflowY = rect.height * (ZOOM_SCALE - 1);

    this.setPan((0.5 - boundedX) * overflowX, (0.5 - boundedY) * overflowY);
  }

  updatePan(clientX, clientY) {
    if (!this.pointerStart) return;
    this.setPan(
      this.pointerStart.panX + clientX - this.pointerStart.x,
      this.pointerStart.panY + clientY - this.pointerStart.y,
    );
  }

  setPan(x, y) {
    const rect = this.container.getBoundingClientRect();
    const maxX = rect.width * (ZOOM_SCALE - 1) / 2;
    const maxY = rect.height * (ZOOM_SCALE - 1) / 2;
    this.panX = Math.max(-maxX, Math.min(maxX, x));
    this.panY = Math.max(-maxY, Math.min(maxY, y));

    this.container.style.setProperty('--zoom-translate-x', `${this.panX}px`);
    this.container.style.setProperty('--zoom-translate-y', `${this.panY}px`);
  }

  close() {
    this.container.classList.remove('is-zoomed');
    this.container.style.removeProperty('--zoom-translate-x');
    this.container.style.removeProperty('--zoom-translate-y');
    this.zoomCanvas?.remove();
    this.zoomCanvas = null;
    this.pointerStart = null;
    this.galleryTouchStart = null;
    this.zoomTouchStart = null;
    this.panX = 0;
    this.panY = 0;

    if (this.button) {
      this.button.setAttribute('aria-pressed', 'false');
      this.button.setAttribute('aria-label', this.zoomInLabel);
    }
  }
}

export default function initProductZoom() {
  document.querySelectorAll('[data-product-zoom-container]').forEach((container) => new ProductZoom(container));
}
